import { useHead } from '@unhead/react';
import { message, Result } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'translations/hooks';

import { useGraphqlApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import { ReportListPageTemplate } from 'business/shift-report/report/components/report-list-template';
import ReportList, {
  DEFAULT_PAGE_LIMIT,
} from 'business/shift-report/report/components/ReportList';
import { useFilterParams } from 'business/shift-report/report/hooks/use-filter-params';
import { getRouteRedirection } from 'business/shift-report/report/services/get-route-redirection';
import Routes from 'config/routes';
import { Module_Enum, useGetReportsToReviewQuery } from 'generated/graphql';
import { useMediaType } from 'technical/media/hooks';
import { PreviousPageButton } from 'ui/button/previous-page-button';
import SectionTitle from 'ui/title/sectionTitle';

import styles from './index.module.scss';

function ReportListToValidate() {
  const { t } = useTranslation();

  useHead({ title: t('user.menu.sections.reporting.title') });

  const { currentConstructionSite } = useAppContext();
  const { isMobile } = useMediaType();

  const { currentPage, getQueryParamString } = useFilterParams();

  const gqlHeaders = useGraphqlApiHeaders(Module_Enum.ShiftReport);

  const { data, loading, error, refetch } = useGetReportsToReviewQuery({
    variables: {
      limit: DEFAULT_PAGE_LIMIT,
      offset: (currentPage - 1) * DEFAULT_PAGE_LIMIT,
      constructionSiteId: currentConstructionSite?.id,
    },
    fetchPolicy: 'network-only',
    skip: !currentConstructionSite?.id,
    context: {
      headers: gqlHeaders,
    },
  });

  useEffect(
    function alertUser() {
      if (error) {
        message.error(error.message);
      }
    },
    [error],
  );

  const emptyListComponent = (
    <Result status="success" title={t('pages.manager.validation.done')} />
  );

  return (
    <ReportListPageTemplate>
      <SectionTitle
        label={t('pages.manager.validation.title')}
        className={styles.title}
        leftButton={
          <PreviousPageButton
            to={{ pathname: Routes.ReportDashboardVisualize }}
            label="common.to_main_page"
            isMobile={isMobile}
          />
        }
      />
      <ReportList
        loading={loading}
        refetchList={refetch}
        shiftReports={data?.shiftReports}
        paginationMeta={{
          shiftReportCount: data?.shiftReportAggregate.aggregate?.count || 0,
        }}
        emptyListComponent={emptyListComponent}
        routeRedirection={getRouteRedirection(
          Routes.ReportListToValidate,
          getQueryParamString(),
        )}
      />
    </ReportListPageTemplate>
  );
}

export default ReportListToValidate;
